import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const fieldStyle = {
  backgroundColor: 'var(--c-pine-soft)',
  border: '1px solid transparent',
  borderRadius: 6,
  color: 'var(--c-ink)',
  fontFamily: 'IBM Plex Sans, sans-serif',
};

const focusOn = (e) => {
  e.target.style.borderColor = 'var(--c-pine)';
  e.target.style.backgroundColor = '#FFFFFF';
  e.target.style.boxShadow = '0 0 0 3px rgba(15, 40, 24, 0.12)';
};
const focusOff = (e) => {
  e.target.style.borderColor = 'transparent';
  e.target.style.backgroundColor = 'var(--c-pine-soft)';
  e.target.style.boxShadow = 'none';
};

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    company: '',
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(formData);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 blueprint-grid"
      style={{ backgroundColor: 'var(--c-bone)' }}
      data-testid="register-page"
    >
      <div
        className="w-full max-w-md bg-white border border-[var(--c-line)] shadow-[0_24px_60px_-24px_rgba(15,40,24,0.18)]"
        style={{ borderRadius: 6 }}
      >
        <div className="px-10 pt-10 pb-8">
          {/* Brand */}
          <div className="flex flex-col items-center gap-3">
            <img
              src="/ecotech-logo.png"
              alt="EcoTech"
              style={{ height: 56, width: 'auto', objectFit: 'contain' }}
            />
            <p
              className="text-sm mt-3"
              style={{ color: 'var(--c-ink-2)', fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              Embodied Carbon Intelligence Portal
            </p>
          </div>

          {/* Heading */}
          <div className="mt-10">
            <h1
              className="text-[28px] font-bold tracking-[0.04em]"
              style={{
                fontFamily: 'Chivo, sans-serif',
                color: 'var(--c-ink)',
                letterSpacing: '0.06em',
              }}
            >
              CREATE ACCOUNT
            </h1>
            <p className="text-sm mt-2" style={{ color: 'var(--c-ink-2)' }}>
              Start calculating embodied carbon today
            </p>
            <div className="hair-divider mt-6" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="mt-7 space-y-5">
            <div>
              <label
                htmlFor="full_name"
                className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2"
                style={{ color: 'var(--c-ink-2)' }}
              >
                Full Name
              </label>
              <input
                id="full_name"
                name="full_name"
                type="text"
                value={formData.full_name}
                onChange={handleChange}
                placeholder="John Smith"
                required
                data-testid="register-name-input"
                className="w-full px-4 py-3 text-[15px] outline-none transition-all"
                style={fieldStyle}
                onFocus={focusOn}
                onBlur={focusOff}
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2"
                style={{ color: 'var(--c-ink-2)' }}
              >
                Work Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@company.com"
                required
                data-testid="register-email-input"
                className="w-full px-4 py-3 text-[15px] outline-none transition-all"
                style={fieldStyle}
                onFocus={focusOn}
                onBlur={focusOff}
              />
            </div>

            <div>
              <label
                htmlFor="company"
                className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2"
                style={{ color: 'var(--c-ink-2)' }}
              >
                Company <span style={{ color: 'var(--c-ink-3)', letterSpacing: 0, textTransform: 'none', fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                id="company"
                name="company"
                type="text"
                value={formData.company}
                onChange={handleChange}
                placeholder="Your architectural firm"
                data-testid="register-company-input"
                className="w-full px-4 py-3 text-[15px] outline-none transition-all"
                style={fieldStyle}
                onFocus={focusOn}
                onBlur={focusOff}
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2"
                style={{ color: 'var(--c-ink-2)' }}
              >
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a secure password (min. 6 chars)"
                required
                minLength={6}
                data-testid="register-password-input"
                className="w-full px-4 py-3 text-[15px] outline-none transition-all"
                style={fieldStyle}
                onFocus={focusOn}
                onBlur={focusOff}
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full font-semibold text-[15px] py-3 mt-2 transition-all"
              style={{
                backgroundColor: loading ? 'var(--c-pine-2)' : 'var(--c-pine)',
                color: '#FFFFFF',
                borderRadius: 6,
                letterSpacing: '0.02em',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 6px 16px -6px rgba(15, 40, 24, 0.45)',
              }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = 'var(--c-pine-2)';
              }}
              onMouseLeave={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = 'var(--c-pine)';
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating account...
                </span>
              ) : (
                'Get Started'
              )}
            </button>
          </form>

          {/* Divider + footer */}
          <div className="hair-divider mt-8" />
          <div className="pt-5 flex items-center justify-between">
            <p className="text-[11px]" style={{ color: 'var(--c-ink-3)' }}>
              © 2026 Switzel Ecotech — Secure Portal
            </p>
            <Link
              to="/login"
              className="text-[12px] font-medium hover:underline"
              style={{ color: 'var(--c-pine)' }}
              data-testid="login-link"
            >
              ← Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
